import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  ArrowRight,
  FileText,
  Calendar,
  User,
  Download,
  Building2,
  Hash,
  FileSignature,
  Upload,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const THEME_COLOR = '#1e3a5f';

export default function AgreementDetails({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [agreement, setAgreement] = useState(null);
  const [loading, setLoading] = useState(true);
  const [company, setCompany] = useState(null);

  useEffect(() => {
    fetchAgreement();
  }, [id]);

  const fetchAgreement = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/agreements/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAgreement(response.data);
      
      // Fetch company info
      if (response.data.company_id) {
        const companyRes = await axios.get(`${API}/companies/${response.data.company_id}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setCompany(companyRes.data);
      }
    } catch (error) {
      toast.error('فشل تحميل تفاصيل الاتفاقية');
      navigate(-1);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error('يُسمح فقط بملفات PDF');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/agreements/${id}/upload`, formData, {
        headers: { 
          'Content-Type': 'multipart/form-data',
          'Authorization': `Bearer ${token}`
        },
      });
      toast.success('تم رفع الملف بنجاح');
      fetchAgreement();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'فشل رفع الملف');
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { label: 'مسودة', bg: 'bg-slate-100', text: 'text-slate-700' },
      review: { label: 'قيد المراجعة', bg: 'bg-amber-100', text: 'text-amber-700' },
      approved: { label: 'معتمد', bg: 'bg-emerald-100', text: 'text-emerald-700' },
      signed: { label: 'موقّع', bg: 'bg-blue-100', text: 'text-blue-700' },
    };
    const config = statusConfig[status] || statusConfig.draft;
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${config.bg} ${config.text}`}>
        {config.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" dir="rtl">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: THEME_COLOR }}></div>
      </div>
    );
  }

  if (!agreement) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center" dir="rtl">
        <p className="text-slate-500">الاتفاقية غير موجودة</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" dir="rtl">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate(-1)}
                className="flex items-center gap-2"
              >
                <ArrowRight size={20} />
                رجوع
              </Button>
              <div className="h-6 w-px bg-slate-200"></div>
              <h1 className="text-xl font-bold text-slate-800">تفاصيل الاتفاقية</h1>
            </div>
            <div className="flex items-center gap-2">
              {getStatusBadge(agreement.status)}
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Basic Info Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileSignature size={20} style={{ color: THEME_COLOR }} />
                  معلومات الاتفاقية
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-slate-500">رقم الاتفاقية</label>
                    <p className="font-medium text-slate-900">{agreement.agreement_number || '-'}</p>
                  </div>
                  <div>
                    <label className="text-sm text-slate-500">عنوان الاتفاقية</label>
                    <p className="font-medium text-slate-900">{agreement.title || '-'}</p>
                  </div>
                </div>
                {agreement.description && (
                  <div>
                    <label className="text-sm text-slate-500">الوصف</label>
                    <p className="font-medium text-slate-900 whitespace-pre-wrap">{agreement.description}</p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Party Info Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <User size={20} style={{ color: THEME_COLOR }} />
                  الطرف الثاني
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="font-medium text-slate-900">{agreement.party_name || '-'}</p>
              </CardContent>
            </Card>

            {/* File Card */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText size={20} style={{ color: THEME_COLOR }} />
                  ملف الاتفاقية
                </CardTitle>
              </CardHeader>
              <CardContent>
                {agreement.file_name ? (
                  <div className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${THEME_COLOR}20` }}>
                        <FileText size={20} style={{ color: THEME_COLOR }} />
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">{agreement.file_name}</p>
                        <p className="text-xs text-slate-500">ملف PDF</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => window.open(`/api/files/agreement/${id}`, '_blank')}
                        data-testid="download-pdf-button"
                      >
                        <Download size={16} className="ml-2" />
                        تحميل
                      </Button>
                      {user?.role === 'legal_manager' && (
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            accept=".pdf"
                            className="hidden"
                            onChange={handleFileUpload}
                          />
                          <Button size="sm" variant="outline" asChild>
                            <span>
                              <Upload size={16} className="ml-2" />
                              تحديث
                            </span>
                          </Button>
                        </label>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="text-center py-8">
                    <FileText size={48} className="mx-auto text-slate-300 mb-3" />
                    <p className="text-slate-500 mb-4">لا يوجد ملف مرفق</p>
                    {user?.role === 'legal_manager' && (
                      <label className="cursor-pointer">
                        <input
                          type="file"
                          accept=".pdf"
                          className="hidden"
                          onChange={handleFileUpload}
                        />
                        <Button style={{ backgroundColor: THEME_COLOR }} className="text-white" asChild>
                          <span>
                            <Upload size={16} className="ml-2" />
                            رفع ملف PDF
                          </span>
                        </Button>
                      </label>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Company Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Building2 size={18} style={{ color: THEME_COLOR }} />
                  الشركة
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="font-medium text-slate-900">{company?.name_ar || '-'}</p>
              </CardContent>
            </Card>

            {/* Dates */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Calendar size={18} style={{ color: THEME_COLOR }} />
                  التواريخ
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <label className="text-sm text-slate-500">تاريخ الاتفاقية</label>
                  <p className="font-medium text-slate-900">
                    {agreement.agreement_date 
                      ? new Date(agreement.agreement_date).toLocaleDateString('ar-SA')
                      : '-'}
                  </p>
                </div>
                <div>
                  <label className="text-sm text-slate-500">تاريخ الإنشاء</label>
                  <p className="font-medium text-slate-900">
                    {new Date(agreement.created_at).toLocaleDateString('ar-SA')}
                  </p>
                </div>
                {agreement.updated_at && (
                  <div>
                    <label className="text-sm text-slate-500">آخر تحديث</label>
                    <p className="font-medium text-slate-900">
                      {new Date(agreement.updated_at).toLocaleDateString('ar-SA')}
                    </p>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Status Info */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-base">
                  <Hash size={18} style={{ color: THEME_COLOR }} />
                  الحالة
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-center gap-2">
                  {getStatusBadge(agreement.status)}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
